import { Mic, MicOff } from '@mui/icons-material';
import {
  Box,
  css,
  IconButton,
  Menu,
  MenuItem,
  styled,
  Typography,
} from '@mui/material';
import type { IconButtonProps } from '@mui/material';
import { memo, useCallback, useRef } from 'react';
import { useRemoteCallButton } from './hooks';
import type { MicButtonState } from './types';
import { useMount, useUnmount } from 'react-use';
import type { RemoteTelephone } from '@tier4/webauto-media-client';

type TelephoneMenuItemProps = {
  telephone: RemoteTelephone;
  onClick: (telephone: RemoteTelephone) => Promise<void>;
};

const TelephoneMenuItem: React.FC<TelephoneMenuItemProps> = ({
  telephone,
  onClick,
}) => {
  return (
    <MenuItem
      disabled={telephone.state !== 'online'}
      onClick={async () => {
        await onClick(telephone);
      }}
    >
      {telephone.tag}
    </MenuItem>
  );
};

type TelephoneCallerProps = {
  telephone: RemoteTelephone;
  onStateChange: (telephone: RemoteTelephone) => void;
};

const TelephoneCaller: React.FC<TelephoneCallerProps> = ({
  telephone,
  onStateChange,
}) => {
  const handleChangeState = useCallback(() => {
    onStateChange(telephone);
  }, [telephone, onStateChange]);

  const isMountedRef = useRef(false);
  const isConnectedRef = useRef(false);

  const disconnect = useCallback(async () => {
    if (!isConnectedRef.current) return;
    isConnectedRef.current = false;
    telephone.removeStateListener(handleChangeState);
    // offline のとき「だけ」disconnect をスキップする。
    // ライブラリの disconnect() は state === 'offline' のときのみ例外を投げるため。
    // ringing / online / calling / talking / error など offline 以外の状態は
    // disconnect が有効なので、ここでは return せずそのまま切断する。
    if (telephone.state === 'offline') return;
    try {
      await telephone.disconnect();
    } catch (error) {
      console.warn('failed to disconnect telephone', error);
    }
  }, [telephone, handleChangeState]);

  useMount(async () => {
    isMountedRef.current = true;
    try {
      // 既に接続済みのデバイスに対して connect を呼ぶと例外になるため状態を確認する
      if (telephone.state === 'offline') {
        await telephone.connect();
      }
      isConnectedRef.current = true;
      telephone.addStateListener(handleChangeState);
      // connect 完了前にアンマウントされていた場合はここで切断する
      if (!isMountedRef.current) {
        await disconnect();
      }
    } catch (error) {
      console.warn('failed to connect telephone', error);
    }
  });

  useUnmount(() => {
    isMountedRef.current = false;
    void disconnect();
  });

  return <></>;
};

type Props = {
  telephones: RemoteTelephone[];
};

const RemoteCallButton: React.FC<Props> = ({ telephones }) => {
  const {
    t,
    state,
    anchorEl,
    usingTelephone,
    handleClickButton,
    handleCloseMenu,
    handleClickMenuItem,
    handleChangeState,
  } = useRemoteCallButton();

  return (
    <Wrapper>
      <StyledButton onClick={handleClickButton} state={state} data-testid="mic-btn">
        {state === 'talking' || state === 'calling' ? (
          <MicOff data-testid="MicOffIcon" />
        ) : (
          <Mic data-testid="MicIcon" />
        )}
      </StyledButton>
      {(state === 'talking' || state === 'calling') && (
        <ButtonStateLabel>{`${usingTelephone?.tag} : ${t(
          `remote.call.state.${state}`,
        )}`}</ButtonStateLabel>
      )}
      {telephones.map((telephone) => (
        <TelephoneCaller
          key={telephone.id}
          telephone={telephone}
          onStateChange={handleChangeState}
        />
      ))}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        {telephones.map((telephone) => (
          <TelephoneMenuItem
            key={telephone.id}
            telephone={telephone}
            onClick={handleClickMenuItem}
          />
        ))}
      </Menu>
    </Wrapper>
  );
};

export default memo(RemoteCallButton);

const Wrapper = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  margin: auto;
  width: 48px;
  height: 48px;
`;

type ButtonProps = IconButtonProps & {
  state: MicButtonState;
};

const StyledButton = styled(IconButton)<ButtonProps>`
  background-color: rgba(245, 9, 9, 0.5);
  color: white;

  &:hover {
    background-color: rgba(245, 9, 9, 0.7);
  }

  ${({ state }: ButtonProps) => {
    if (state === 'calling') {
      return css`
        background-color: rgba(255, 160, 0, 0.7);

        &:hover {
          background-color: rgba(255, 160, 0, 0.9);
        }
      `;
    }
    if (state === 'talking') {
      return css`
        background-color: rgba(67, 160, 71, 0.7);

        &:hover {
          background-color: rgba(67, 160, 71, 0.9);
        }
      `;
    }
  }}
`;

const ButtonStateLabel = styled(Typography)`
  text-align: center;
  color: white;
  width: 80px;
  margin-top: 6px;
  margin-left: calc((48px - 80px) / 2);
  user-select: none;
  pointer-events: none;
  font-size: 0.5rem;
  line-height: 1;
`;
